import React, { useEffect, useState } from 'react';
import { API } from '../api';
import { defaultProductPicture, fileToDataUrl } from '../imageUtils';

const emptyProductForm = {
  id: null,
  name: '',
  reference_number: '',
  picture: ''
};

export default function Admin({ token }) {
  const [productForm, setProductForm] = useState(emptyProductForm);
  const [editingProduct, setEditingProduct] = useState(false);
  const [unitName, setUnitName] = useState('');
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [units, setUnits] = useState([]);
  const [error, setError] = useState('');

  const updateProductForm = (key, value) => {
    setProductForm((current) => ({ ...current, [key]: value }));
  };

  const loadProducts = () => {
    fetch(`${API}/products`, { headers: { Authorization: token } })
      .then((res) => res.json())
      .then((data) => {
        setProducts(data);
        setSelectedProduct((current) => {
          if (!current) return data[0] || null;
          return data.find((product) => product.id === current.id) || data[0] || null;
        });
      })
      .catch(() => setError('Could not load products.'));
  };

  const loadUnits = (productId) => {
    if (!productId) {
      setUnits([]);
      return;
    }

    fetch(`${API}/products/${productId}/units`, { headers: { Authorization: token } })
      .then((res) => res.json())
      .then(setUnits)
      .catch(() => setError('Could not load units.'));
  };

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    loadUnits(selectedProduct?.id);
  }, [selectedProduct?.id]);

  const resetProductForm = () => {
    setProductForm(emptyProductForm);
    setEditingProduct(false);
  };

  const startProductEdit = (product) => {
    setSelectedProduct(product);
    setProductForm({
      id: product.id,
      name: product.name,
      reference_number: product.reference_number || '',
      picture: product.picture || ''
    });
    setEditingProduct(true);
  };

  const saveProduct = async () => {
    if (!productForm.name.trim()) return;
    setError('');

    const res = await fetch(`${API}/products${editingProduct ? `/${productForm.id}` : ''}`, {
      method: editingProduct ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: token },
      body: JSON.stringify({
        name: productForm.name.trim(),
        reference_number: productForm.reference_number.trim() || null,
        picture: productForm.picture || null
      })
    });

    if (!res.ok) {
      setError('Could not save product. The name or reference number may already exist.');
      return;
    }

    resetProductForm();
    loadProducts();
  };

  const addUnit = async () => {
    if (!selectedProduct || !unitName.trim()) return;
    setError('');

    const res = await fetch(`${API}/products/${selectedProduct.id}/units`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: token },
      body: JSON.stringify({ name: unitName.trim() })
    });

    if (!res.ok) {
      setError('Could not add unit. It may already exist for this product.');
      return;
    }

    setUnitName('');
    loadUnits(selectedProduct.id);
    loadProducts();
  };

  const removeUnit = async (unitId) => {
    if (!selectedProduct) return;

    const res = await fetch(`${API}/products/${selectedProduct.id}/units/${unitId}`, {
      method: 'DELETE',
      headers: { Authorization: token }
    });

    if (!res.ok) {
      setError('Could not remove unit.');
      return;
    }

    loadUnits(selectedProduct.id);
    loadProducts();
  };

  return (
    <div className="wideContainer">
      <h2>Admin</h2>
      {error && <p className="error">{error}</p>}

      <div className="adminLayout">
        <section className="adminPanel">
          <h3>{editingProduct ? 'Edit Product' : 'Create Product'}</h3>
          <input value={productForm.name} onChange={(e) => updateProductForm('name', e.target.value)} placeholder="Product name" />
          <input
            value={productForm.reference_number}
            onChange={(e) => updateProductForm('reference_number', e.target.value)}
            placeholder="Product reference number"
          />
          <div className="imageField">
            <img className="imagePreview" src={productForm.picture || defaultProductPicture} alt="" />
            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                if (e.target.files?.[0]) updateProductForm('picture', await fileToDataUrl(e.target.files[0]));
              }}
            />
          </div>
          {productForm.picture && <button className="secondaryButton" onClick={() => updateProductForm('picture', '')}>Use Default Picture</button>}
          <button onClick={saveProduct}>{editingProduct ? 'Save Product' : 'Add Product'}</button>
          {editingProduct && <button className="secondaryButton" onClick={resetProductForm}>Cancel Edit</button>}

          <h3>Products</h3>
          <div className="productList">
            {products.map((product) => (
              <div className={`productRow ${selectedProduct?.id === product.id ? 'active' : ''}`} key={product.id}>
                <button className="productSelect" onClick={() => setSelectedProduct(product)}>
                  <img className="productThumb" src={product.picture || defaultProductPicture} alt="" />
                  <span>{product.name}</span>
                  <small>{product.reference_number || 'No reference'} · {product.unit_count || 0} units</small>
                </button>
                <button className="smallButton" onClick={() => startProductEdit(product)}>Edit</button>
              </div>
            ))}
          </div>
        </section>

        <section className="adminPanel">
          <h3>{selectedProduct ? `${selectedProduct.name} Units` : 'Product Units'}</h3>
          {selectedProduct ? (
            <>
              <div className="inlineForm">
                <input value={unitName} onChange={(e) => setUnitName(e.target.value)} placeholder="Unit name" />
                <button onClick={addUnit}>Add Unit</button>
              </div>

              <div className="unitList">
                {units.map((unit) => (
                  <div className="unitRow" key={unit.id}>
                    <span>{unit.name}</span>
                    <button className="smallButton" onClick={() => removeUnit(unit.id)}>Remove</button>
                  </div>
                ))}
                {units.length === 0 && <p>No units for this product yet.</p>}
              </div>
            </>
          ) : (
            <p>Create or select a product to manage its units.</p>
          )}
        </section>
      </div>
    </div>
  );
}
